const normalizeString = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

export const canAccessSegment = (user, segmentName) => {
  if (!user || !segmentName) return false;
  const segments = user?.access?.segments;

  if (segments === "all") return true;
  if (!Array.isArray(segments)) return false;

  return segments.some(
    (seg) => normalizeString(seg) === normalizeString(segmentName)
  );
};

const segmentRouteMap = [
  { pattern: /^\/cms\/trek/i, segment: "Trek" },
  { pattern: /^\/cms\/travel/i, segment: "Travel" },
  { pattern: /^\/cms\/tour/i, segment: "Tour" },
  { pattern: /^\/cms\/upcoming-event/i, segment: "Upcoming Events" },
  { pattern: /^\/cms\/youtube-video/i, segment: "Youtube Videos" },
  { pattern: /^\/cms\/image-gallery/i, segment: "Image Gallery" },
  { pattern: /^\/cms\/appearance/i, segment: "Appearance" },
];

export const segmentFromPath = (pathname = "") => {
  const match = segmentRouteMap.find((route) => route.pattern.test(pathname));
  return match?.segment || null;
};

export const canAccessPath = (pathname, user) => {
  if (!user) return false;
  const isSuperAdmin = user.role === "superAdmin" || user.isSuperAdmin;
  if (isSuperAdmin) return true;

  // Allow District Manager/Sub Admin to access their own team profile page
  const profileMatch = pathname.match(/^\/roleManager\/profile\/([^/]+)/i);
  if (profileMatch) {
    const requestedId = profileMatch[1];
    if (requestedId === user.id && (user.role === "subAdmin" || user.role === "districtManager")) {
      return true;
    }
    return false;
  }

  // Lock down other admin-only areas
  const restrictedForNonSuper = [/^\/dashboard/i, /^\/roleManager/i];
  if (restrictedForNonSuper.some((rgx) => rgx.test(pathname))) return false;

  const segment = segmentFromPath(pathname);
  if (segment) {
    if (segment === "Appearance") {
      return !!(user.role === "superAdmin" || user.isSuperAdmin);
    }
    return canAccessSegment(user, segment);
  }

  return true;
};

const extractLocationKey = (item) => {
  const key =
    item?.district ||
    item?.location ||
    item?.address?.district ||
    item?.address?.state ||
    "";
  return normalizeString(key);
};

export const filterByLocationAccess = (items = [], user) => {
  if (!user?.access || user.access.locations === "all") return items;

  const allowed = (user.access.locations || [])
    .map(normalizeString)
    .filter(Boolean);

  if (!allowed.length) return [];

  return items.filter((item) => {
    const key = extractLocationKey(item);
    return key && allowed.includes(key);
  });
};

export const projectSegmentsForUser = (segments = [], user, options = {}) => {
  const { hideBlocked = false } = options;
  const isSuperAdmin = !!(user?.role === "superAdmin" || user?.isSuperAdmin);

  const projected = segments.map((segment) => {
    const blocked = segment.superAdminOnly
      ? !isSuperAdmin
      : user
        ? !canAccessSegment(user, segment.title)
        : true;
    return { ...segment, disabled: segment.disabled || blocked };
  });

  // For limited roles (e.g., district manager, editor), hide segments they can't access
  return hideBlocked ? projected.filter((segment) => !segment.disabled) : projected;
};
